<template>
    <div>
        <div>
            <report-header
                :title="title"
                :isLoading="isLoading"
                :reportPagination="pagination"
                :isShowDateRange="true"
                :formatDateRangeTitle="dateRange"
            />

            <b-card id="report-card">
                <div id="report-body" class="w-100">
                    <div v-if="showDummyTable || results.length == 0">
                        <p class="item-name" v-if="showDummyTable">
                            Loading providers..
                        </p>
                        <QTable
                            :columns="formattedColumns"
                            :data="[]"
                            hide-bottom
                            :loading="isLoading"
                            :rows-per-page-options="[0]"
                        >
                            <template
                                v-slot:bottom-row
                                v-if="
                                    results.length == 0 &&
                                    !isLoading &&
                                    !isError
                                "
                            >
                                <q-tr>
                                    <q-td colspan="8"
                                        >No records available</q-td
                                    >
                                </q-tr>
                            </template>
                        </QTable>
                    </div>
                    <div v-else>
                        <div v-for="(result, i) in results" :key="i">
                            <p class="item-name">{{ result.name }}</p>
                            <QTable
                                :columns="formattedColumns"
                                :data="result.data"
                                hide-bottom
                                :loading="isLoading"
                                :rows-per-page-options="[0]"
                            >
                                <template v-slot:header-cell="props">
                                    <q-th :props="props">
                                        <strong>{{ props.col.label }}</strong>
                                    </q-th>
                                </template>
                            </QTable>
                        </div>
                    </div>
                </div>
            </b-card>
        </div>
    </div>
</template>
<script>
import { QTable } from "quasar";
import { merge, find } from "lodash";
import Report from "@/mixins/report";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import "@/css/quasar-table.css";

const DISPLAY_MONTHLY = "month";

export default {
    mixins: [Report],
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("month"),
                    end: this.getEndDate("day"),
                    datePickerOptions: {
                        format: this.getDateFormat()
                    }
                };
            }
        }
    },
    data() {
        return {
            isLoading: true,
            isError: false,
            showDummyTable: true,
            locale: "en-US",
            results: [],
            title: "Transactions By Provider",
            dateRange: "",
            indexAxios: 0,
            abortController: [],
            filterBySelected: DISPLAY_MONTHLY,
            formattedColumns: [
                {},
                {
                    name: "patients_seen",
                    label: "Patients Seen",
                    field: "patients_seen",
                    align: "center",
                    style: "white-space: nowrap"
                },
                {
                    name: "clients_seen",
                    label: "Clients Seen",
                    field: "clients_seen",
                    align: "center",
                    style: "white-space: nowrap"
                },
                {
                    name: "patient_visits",
                    label: "Patient Visits",
                    field: "patient_visits",
                    align: "center",
                    style: "white-space: nowrap"
                },
                {
                    name: "client_visits",
                    label: "Client Visits",
                    field: "client_visits",
                    align: "center",
                    style: "white-space: nowrap"
                },
                {
                    name: "total_invoiced",
                    label: "Total Invoiced",
                    field: row => row.invoiced_dollars,
                    format: (val, row) => {
                        return this._toCurrency(row.invoiced_dollars);
                    },
                    align: "center"
                },
                {
                    name: "avg_patient_invoice",
                    label: "Avg Patient Invoice",
                    field: row => row.dollars_per_patient,
                    format: (val, row) => {
                        return this._toCurrency(row.dollars_per_patient);
                    },
                    align: "center"
                },
                {
                    name: "avg_client_invoice",
                    label: "Avg Client Invoice",
                    field: row => row.dollars_per_client,
                    format: (val, row) => {
                        return this._toCurrency(row.dollars_per_client);
                    },
                    align: "center"
                }
            ],
            monthlyColumns: {
                name: "item_create_month",
                label: "Month",
                field: "item_create_month",
                align: "center",
                style: "white-space: nowrap",
                key: "header-1"
            },
            weeklyColumns: {
                name: "item_create_week",
                label: "Week",
                field: "item_create_week",
                align: "center",
                style: "white-space: nowrap"
            }
        };
    },
    mounted() {
        let self = this;

        self.data.isShowDateRange = true;
        self.data.isShowFilter = true;
        self.data.datePickerOptions.format = this.getDateFormat();
        self.data.datePickerOptionDefault = 3;

        self._getSetting().then(() => {
            self.data.start = self.getStartDate("month");
            self.data.end = self.getEndDate("month");
            self.data.from = self.converToUnix(self.data.start);
            self.data.to = self.converToUnix(self.data.end);
            self.defaultToggleOptions(false);
            self.fetchData(self.indexAxios);
        });

        self.$root.$on("report-filter-date-range:refresh", self.init);
    },
    beforeDestroy: function () {
        this.data.datePickerOptions.format = this.getDateFormat();
        this.$root.$off("report-filter-date-range:refresh", this.init);
        if (this.abortController.length > 0) {
            this.abortController[this.indexAxios].abort();
        }
    },
    methods: {
        init(params) {
            if (this.abortController.length > 0) {
                this.abortController[this.indexAxios].abort();
            }

            this.indexAxios++;

            if (params) {
                this.data.from = params.from;
                this.data.to = params.to;
                this.data.start = params.start;
                this.data.end = params.end;
                this.data.filterByOptionDefault = params.filterBy.value;
                this.filterBySelected = params.filterBy.value;
            }

            this.resetPagination();
            this.fetchData(this.indexAxios);
        },
        async fetchData(indexAxios, page = 1) {
            let self = this;
            self.isLoading = true;
            self.isError = false;
            self.pagination.page = page;

            if (self.pagination.page == 1) {
                self.abortController[indexAxios] = new AbortController();
                self.defaultToggleOptions(false);
                self.results = [];
                self.dateRange = self.getDateRange(
                    self.data.from,
                    self.data.to,
                    self.formatInMonth
                );
                if (self.filterBySelected == DISPLAY_MONTHLY) {
                    self.formattedColumns[0] = self.monthlyColumns;
                } else {
                    self.formattedColumns[0] = self.weeklyColumns;
                }
            }
            self.loadingPercentages();

            await ReportService.getReport(
                CREPORT.TYPE.TRANSACTION_BY_PROVIDER,
                {
                    from: self.data.from,
                    to: self.data.to,
                    refresh: self.refresh,
                    perPage: this.RECORD_PER_PAGE,
                    page: self.pagination.page,
                    filterBy: self.filterBySelected,
                    signal: self.abortController[indexAxios].signal
                }
            )
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        self.renderReport(res.resource.results.data);
                        if (self.pagination.page == 1) {
                            self.dateRange = self.getDateRange(
                                res.resource.meta.from,
                                res.resource.meta.to,
                                self.formatInMonth
                            );
                            self.title = res.resource.meta.title;
                            self.dynamicPageTitle(self.title);
                            self.showDummyTable = false;
                        }
                        self.pagination.lastPage =
                            res.resource.results.meta.lastPage;
                        if (self.pagination.page < self.pagination.lastPage) {
                            self.fetchData(indexAxios, ++self.pagination.page);
                        } else {
                            self.loadingCompleted();
                            self.timer(1000).then(() => {
                                self.defaultToggleOptions(true);
                                self.isLoading = false;
                            });
                        }
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    if (!self.abortController[indexAxios].signal.aborted) {
                        console.error(e.message);
                        self.isLoading = false;
                        self.loadingCompleted();
                        self.defaultToggleOptions(true);
                        self.isError = true;
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    }
                });
        },
        renderReport(data) {
            let self = this;
            let dataArray = _.isArray(data) ? data : [];
            dataArray.forEach(function (obj) {
                let doctorObject = find(self.results, {
                    name: obj.doctor_name
                });
                if (!doctorObject) {
                    self.results.push({
                        name: obj.doctor_name,
                        data: [obj]
                    });
                } else {
                    doctorObject.data.push(obj);
                }
            });
        }
    }
};
</script>
